/**
 * Device Connection Manager Service
 *
 * Manages WebHID device lifecycle including connection, disconnection,
 * and device state tracking. Handles device enumeration, filtering,
 * and integration with the IMX Loader service.
 */

import {
  ILogger,
  IHIDDevice,
  IHIDNavigator,
  IHIDConnectionEvent,
  createBrowserHIDNavigator,
} from '../types/imx-types';
import {
  UpdaterError,
  ErrorCode,
  WizardStep,
  RecoveryAction,
  DeviceInfo,
} from '../types/wizard-types';
import { IMXLoaderService } from './imx-loader.service';

/**
 * Device filter for i.MX devices
 */
export interface DeviceFilter {
  vendorId?: number;
  productId?: number;
}

/**
 * Connection state
 */
export enum ConnectionState {
  DISCONNECTED = 'disconnected',
  REQUESTING = 'requesting',
  CONNECTED = 'connected',
  CONNECTING = 'connecting',
  FAILED = 'failed',
}

/**
 * Connection event
 */
export interface ConnectionEvent {
  state: ConnectionState;
  device?: IHIDDevice;
  error?: UpdaterError;
}

/**
 * Connection listener callback
 */
export type ConnectionListener = (event: ConnectionEvent) => void;

/**
 * Device Connection Manager
 *
 * Handles WebHID device discovery, connection, and lifecycle management.
 * Integrates with IMXLoaderService for device communication.
 *
 * Supports dependency injection of IHIDNavigator for testing without
 * physical devices.
 */
export class DeviceConnectionManager {
  private device: IHIDDevice | null = null;
  private connectionState: ConnectionState = ConnectionState.DISCONNECTED;
  private listeners: Set<ConnectionListener> = new Set();
  private disconnectHandler: ((event: IHIDConnectionEvent) => void) | null = null;
  private hidNavigator: IHIDNavigator | null;

  // Known i.MX device configurations
  private readonly IMX_VENDOR_IDS = [0x15a2, 0x1fc9]; // Freescale/NXP
  private readonly ROM_MODE_PRODUCT_IDS = [0x007d, 0x0080, 0x013d]; // i.MX6/7 ROM mode

  /**
   * Create a new DeviceConnectionManager
   *
   * @param logger - Logger for device operations
   * @param imxLoader - IMX loader service for device communication
   * @param hidNavigator - Optional HID navigator (defaults to browser navigator.hid)
   */
  constructor(
    private logger: ILogger,
    private imxLoader: IMXLoaderService,
    hidNavigator?: IHIDNavigator | null
  ) {
    // Use provided navigator or create browser navigator
    this.hidNavigator = hidNavigator !== undefined ? hidNavigator : createBrowserHIDNavigator();
    this.checkBrowserSupport();
  }

  // ============================================================================
  // Connection Management
  // ============================================================================

  /**
   * Request device from user and connect
   */
  async requestDevice(filters?: DeviceFilter[]): Promise<DeviceInfo> {
    this.logger.info('Requesting device from user');

    // Check browser support
    if (!this.isBrowserSupported()) {
      throw new UpdaterError(
        'WebHID is not supported in this browser. Please use Chrome, Edge, or Opera.',
        ErrorCode.BROWSER_NOT_SUPPORTED,
        WizardStep.DEVICE_CONNECTION,
        [
          {
            action: RecoveryAction.CONTACT_SUPPORT,
            label: 'Use Supported Browser',
            description: 'Switch to Chrome, Edge, or Opera (version 89+)',
            order: 1,
          },
        ]
      );
    }

    this.setConnectionState(ConnectionState.REQUESTING);

    try {
      // Build filter for device picker
      const hidFilters = this.buildDeviceFilters(filters);

      // Request device from user
      const devices = await this.hidNavigator!.requestDevice({
        filters: hidFilters,
      });

      if (devices.length === 0) {
        throw new UpdaterError(
          'No device selected',
          ErrorCode.NO_DEVICE_SELECTED,
          WizardStep.DEVICE_CONNECTION,
          [
            {
              action: RecoveryAction.RETRY_STEP,
              label: 'Try Again',
              description: 'Click to select a device',
              order: 1,
            },
          ],
          'User cancelled device selection'
        );
      }

      const device = devices[0];
      this.logger.info('Device selected', {
        vendorId: device.vendorId,
        productId: device.productId,
        productName: device.productName,
      });

      // Connect to device
      return await this.connectToDevice(device);
    } catch (error) {
      this.setConnectionState(ConnectionState.FAILED);

      if (error instanceof UpdaterError) {
        throw error;
      }

      // Wrap unexpected errors
      throw new UpdaterError(
        'Failed to request device',
        ErrorCode.DEVICE_OPEN_FAILED,
        WizardStep.DEVICE_CONNECTION,
        [
          {
            action: RecoveryAction.RETRY_STEP,
            label: 'Retry Connection',
            description: 'Try connecting to the device again',
            order: 1,
          },
          {
            action: RecoveryAction.POWER_CYCLE,
            label: 'Power Cycle Device',
            description: 'Unplug and replug the device',
            order: 2,
          },
        ],
        error instanceof Error ? error.message : String(error)
      );
    }
  }

  /**
   * Connect to a specific device
   */
  async connectToDevice(hidDevice: IHIDDevice): Promise<DeviceInfo> {
    this.logger.info('Connecting to device', {
      vendorId: hidDevice.vendorId,
      productId: hidDevice.productId,
    });

    this.setConnectionState(ConnectionState.CONNECTING);

    try {
      // Verify device is an i.MX device
      if (!this.isIMXDevice(hidDevice)) {
        throw new UpdaterError(
          'Selected device is not a supported i.MX device',
          ErrorCode.WRONG_DEVICE,
          WizardStep.DEVICE_CONNECTION,
          [
            {
              action: RecoveryAction.RETRY_STEP,
              label: 'Select Correct Device',
              description: 'Choose the G5 force plate in recovery mode',
              order: 1,
            },
          ],
          `Device: ${hidDevice.vendorId}:${hidDevice.productId}`
        );
      }

      // Open device
      if (!hidDevice.opened) {
        await hidDevice.open();
      }

      // Store device reference
      this.device = hidDevice;

      // Set up disconnect handler
      this.setupDisconnectHandler();

      // Connect via IMX Loader to get device configuration
      const config = await this.imxLoader.connectDevice(hidDevice);

      // Determine device stage
      const stage = this.detectDeviceStage(hidDevice.productId);

      // Build device info
      const deviceInfo: DeviceInfo = {
        vendorId: hidDevice.vendorId,
        productId: hidDevice.productId,
        productName: hidDevice.productName || 'Unknown',
        stage,
        config,
      };

      this.setConnectionState(ConnectionState.CONNECTED, hidDevice);
      this.logger.success('Device connected successfully', deviceInfo);

      return deviceInfo;
    } catch (error) {
      this.setConnectionState(ConnectionState.FAILED);

      if (error instanceof UpdaterError) {
        throw error;
      }

      throw new UpdaterError(
        'Failed to connect to device',
        ErrorCode.DEVICE_OPEN_FAILED,
        WizardStep.DEVICE_CONNECTION,
        [
          {
            action: RecoveryAction.RETRY_STEP,
            label: 'Retry Connection',
            description: 'Try connecting again',
            order: 1,
          },
          {
            action: RecoveryAction.CHECK_CONNECTION,
            label: 'Check USB Connection',
            description: 'Ensure device is properly connected',
            order: 2,
          },
        ],
        error instanceof Error ? error.message : String(error)
      );
    }
  }

  /**
   * Disconnect from current device
   */
  async disconnect(): Promise<void> {
    if (!this.device) {
      this.logger.warning('No device to disconnect');
      return;
    }

    this.logger.info('Disconnecting device');

    try {
      if (this.device.opened) {
        await this.device.close();
      }

      this.cleanup();
      this.setConnectionState(ConnectionState.DISCONNECTED);
      this.logger.info('Device disconnected');
    } catch (error) {
      this.logger.error('Error disconnecting device', error);
      this.cleanup();
      this.setConnectionState(ConnectionState.DISCONNECTED);
    }
  }

  /**
   * Reconnect to device (after re-enumeration)
   */
  async reconnect(timeout: number = 30000): Promise<DeviceInfo> {
    this.logger.info('Waiting for device re-enumeration', { timeout });

    const startTime = Date.now();

    // Poll for device to reappear
    while (Date.now() - startTime < timeout) {
      try {
        // Get all connected HID devices
        const devices = await this.hidNavigator!.getDevices();

        // Look for i.MX device
        const imxDevice = devices.find((d) => this.isIMXDevice(d));

        if (imxDevice) {
          this.logger.info('Device re-enumerated', {
            vendorId: imxDevice.vendorId,
            productId: imxDevice.productId,
          });

          return await this.connectToDevice(imxDevice);
        }

        // Wait before polling again
        await this.sleep(1000);
      } catch (error) {
        // Fatal errors should fail fast instead of waiting for timeout
        if (this.isFatalReconnectError(error)) {
          this.logger.error('Fatal error during reconnect polling', error);
          throw error instanceof UpdaterError
            ? error
            : new UpdaterError(
                'Permission denied or device access revoked',
                ErrorCode.DEVICE_OPEN_FAILED,
                WizardStep.EASY_INSTALLER_LOADING,
                [
                  {
                    action: RecoveryAction.RETRY_STEP,
                    label: 'Grant Permission',
                    description: 'Click to re-select and grant access to the device',
                    order: 1,
                  },
                  {
                    action: RecoveryAction.START_OVER,
                    label: 'Start Over',
                    description: 'Restart the update process',
                    order: 2,
                  },
                ],
                error instanceof Error ? error.message : String(error)
              );
        }
        // Device not found yet - continue polling
        this.logger.debug('Error during reconnect polling', error);
      }
    }

    // Timeout reached
    throw new UpdaterError(
      'Device did not re-enumerate after bootloader load',
      ErrorCode.DEVICE_NOT_RE_ENUMERATED,
      WizardStep.EASY_INSTALLER_LOADING,
      [
        {
          action: RecoveryAction.POWER_CYCLE,
          label: 'Power Cycle Device',
          description: 'Unplug device, wait 5 seconds, then replug',
          order: 1,
        },
        {
          action: RecoveryAction.START_OVER,
          label: 'Start Over',
          description: 'Restart the update process from the beginning',
          order: 2,
        },
      ],
      `Timeout after ${timeout}ms`
    );
  }

  // ============================================================================
  // State Access
  // ============================================================================

  /**
   * Get current connection state
   */
  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Check if device is connected
   */
  isConnected(): boolean {
    return this.connectionState === ConnectionState.CONNECTED && this.device !== null;
  }

  /**
   * Get current device
   */
  getDevice(): IHIDDevice | null {
    return this.device;
  }

  // ============================================================================
  // Event Listeners
  // ============================================================================

  /**
   * Subscribe to connection events
   */
  subscribe(listener: ConnectionListener): () => void {
    this.listeners.add(listener);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  // ============================================================================
  // Private Helpers
  // ============================================================================

  /**
   * Check if browser supports WebHID
   */
  private isBrowserSupported(): boolean {
    return this.hidNavigator !== null;
  }

  /**
   * Check browser support and log warning if not supported
   */
  private checkBrowserSupport(): void {
    if (!this.isBrowserSupported()) {
      this.logger.warning(
        'WebHID is not supported in this browser. Use Chrome, Edge, or Opera (version 89+).'
      );
    }
  }

  /**
   * Build HID device filters
   */
  private buildDeviceFilters(customFilters?: DeviceFilter[]): HIDDeviceFilter[] {
    if (customFilters && customFilters.length > 0) {
      return customFilters as HIDDeviceFilter[];
    }

    // Default: filter for i.MX devices in ROM mode
    const filters: HIDDeviceFilter[] = [];

    this.IMX_VENDOR_IDS.forEach((vendorId) => {
      this.ROM_MODE_PRODUCT_IDS.forEach((productId) => {
        filters.push({ vendorId, productId });
      });
    });

    return filters;
  }

  /**
   * Check if device is a known i.MX device
   */
  private isIMXDevice(device: IHIDDevice): boolean {
    return this.IMX_VENDOR_IDS.includes(device.vendorId);
  }

  /**
   * Detect device stage based on product ID
   */
  private detectDeviceStage(productId: number): 'rom' | 'uboot' | 'unknown' {
    if (this.ROM_MODE_PRODUCT_IDS.includes(productId)) {
      return 'rom';
    }

    // U-Boot typically uses product ID 0x0001
    if (productId === 0x0001) {
      return 'uboot';
    }

    return 'unknown';
  }

  /**
   * Set up disconnect handler
   */
  private setupDisconnectHandler(): void {
    if (!this.device || !this.hidNavigator) return;

    this.disconnectHandler = (event: IHIDConnectionEvent) => {
      this.logger.warning('Device disconnected unexpectedly');

      this.cleanup();
      this.setConnectionState(
        ConnectionState.FAILED,
        undefined,
        new UpdaterError(
          'Device disconnected during update',
          ErrorCode.DEVICE_DISCONNECTED,
          WizardStep.DEVICE_CONNECTION,
          [
            {
              action: RecoveryAction.CHECK_CONNECTION,
              label: 'Reconnect Device',
              description: 'Check USB cable and reconnect device',
              order: 1,
            },
            {
              action: RecoveryAction.START_OVER,
              label: 'Start Over',
              description: 'Restart the update process',
              order: 2,
            },
          ]
        )
      );
    };

    this.hidNavigator.addEventListener('disconnect', this.disconnectHandler);
  }

  /**
   * Clean up resources
   */
  private cleanup(): void {
    if (this.disconnectHandler && this.hidNavigator) {
      this.hidNavigator.removeEventListener('disconnect', this.disconnectHandler);
      this.disconnectHandler = null;
    }

    this.device = null;
  }

  /**
   * Set connection state and notify listeners
   */
  private setConnectionState(
    state: ConnectionState,
    device?: IHIDDevice,
    error?: UpdaterError
  ): void {
    this.connectionState = state;

    const event: ConnectionEvent = { state, device, error };

    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (err) {
        this.logger.error('Error in connection listener', err);
      }
    });
  }

  /**
   * Sleep helper for reconnect polling
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Check if an error during reconnect polling is fatal and should fail immediately
   * rather than continuing to poll until timeout.
   *
   * Fatal errors include:
   * - Permission denied (NotAllowedError) - user revoked access
   * - Security errors - browser security policy violation
   * - Device open failures from UpdaterError
   */
  private isFatalReconnectError(error: unknown): boolean {
    // Check for DOMException with fatal error names
    if (error instanceof DOMException) {
      const fatalDOMErrors = ['NotAllowedError', 'SecurityError', 'InvalidStateError'];
      return fatalDOMErrors.includes(error.name);
    }

    // Check for UpdaterError with fatal codes
    if (error instanceof UpdaterError) {
      const fatalCodes = [
        ErrorCode.DEVICE_OPEN_FAILED,
        ErrorCode.BROWSER_NOT_SUPPORTED,
        ErrorCode.WRONG_DEVICE,
      ];
      return fatalCodes.includes(error.code);
    }

    return false;
  }
}
